/**
 * Dropdown Menu Component
 *
 * A menu component for actions, typically triggered by a button.
 * Used for action menus, context menus, and navigation dropdowns.
 */

import { useState, useRef, useEffect, useId, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import type { MouseEvent as ReactMouseEvent, ReactElement } from 'react';
import { cn } from '../../lib/cn';
import type {
  DropdownMenuProps,
  DropdownMenuItem,
  DropdownMenuDivider,
  DropdownMenuLabel,
  DropdownMenuItemType,
} from './dropdown-menu.types';

// Type guards
const isDivider = (item: DropdownMenuItemType): item is DropdownMenuDivider =>
  'type' in item && item.type === 'divider';

const isLabel = (item: DropdownMenuItemType): item is DropdownMenuLabel =>
  'type' in item && item.type === 'label';

const isMenuItem = (item: DropdownMenuItemType): item is DropdownMenuItem =>
  !('type' in item);

type MenuPosition = {
  left: number;
  top: number;
  transform?: string;
};

export const DropdownMenu = ({
  trigger,
  items,
  align = 'end',
  side = 'bottom',
  disabled = false,
  minWidth = 180,
  className,
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Get only clickable items for keyboard navigation
  const clickableItems = items.filter(isMenuItem).filter((item) => !item.disabled);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    const updateMenuPosition = () => {
      const triggerNode = triggerRef.current;
      if (!triggerNode) return;

      const rect = triggerNode.getBoundingClientRect();
      const horizontalOffset =
        align === 'start'
          ? rect.left
          : align === 'center'
            ? rect.left + rect.width / 2 - minWidth / 2
            : rect.right - minWidth;

      setMenuPosition({
        left: Math.max(8, horizontalOffset),
        top: side === 'top' ? rect.top - 4 : rect.bottom + 4,
        transform: side === 'top' ? 'translateY(-100%)' : undefined,
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [align, isOpen, minWidth, side]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev + 1;
            return next >= clickableItems.length ? 0 : next;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? clickableItems.length - 1 : next;
          });
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (highlightedIndex >= 0 && clickableItems[highlightedIndex]) {
            clickableItems[highlightedIndex].onClick?.();
            setIsOpen(false);
            triggerRef.current?.focus();
          }
          break;
        case 'Home':
          event.preventDefault();
          setHighlightedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setHighlightedIndex(Math.max(clickableItems.length - 1, 0));
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, clickableItems]);

  // Reset highlighted index when menu opens/closes
  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }

    menuRef.current?.focus();
  }, [isOpen, menuPosition]);

  const handleTriggerClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleItemClick = (item: DropdownMenuItem) => {
    if (item.disabled) return;
    item.onClick?.();
    setIsOpen(false);
  };

  // Clone trigger to add click handler
  const triggerElement = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<any>, {
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': isOpen,
        'aria-controls': isOpen ? menuId : undefined,
        onClick: (event: ReactMouseEvent) => {
          (trigger as ReactElement<any>).props.onClick?.(event);
          handleTriggerClick();
        },
        disabled: disabled,
      })
    : trigger;

  return (
    <div className="relative inline-block" ref={containerRef}>
      {triggerElement}

      {isOpen && menuPosition
        ? createPortal(
            <div
              id={menuId}
              ref={menuRef}
              className={cn(
                'fixed z-[1100] max-h-[min(24rem,calc(100vh-2rem))] overflow-y-auto rounded-xl border border-[var(--a-line)] bg-[var(--a-surface)] p-1 text-[var(--a-text)] shadow-[var(--a-shadow-popover)] [font-family:var(--a-body)] focus:outline-none',
                className,
              )}
              style={{
                minWidth,
                left: menuPosition.left,
                top: menuPosition.top,
                transform: menuPosition.transform,
              }}
              role="menu"
              aria-orientation="vertical"
              tabIndex={-1}
            >
              {items.map((item) => {
                if (isDivider(item)) {
                  return <div key={item.key} className="my-1 h-px bg-[var(--a-line-soft)]" role="separator" />;
                }

                if (isLabel(item)) {
                  return (
                    <div
                      key={item.key}
                      className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--a-text-faint)]"
                    >
                      {item.label}
                    </div>
                  );
                }

                // It's a menu item
                const clickableIndex = clickableItems.findIndex((ci) => ci.key === item.key);
                const isHighlighted = clickableIndex === highlightedIndex;

                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    className={cn(
                      'flex min-h-9 w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] leading-5 transition-colors duration-150 focus:outline-none',
                      item.disabled && 'cursor-not-allowed opacity-50',
                      item.destructive
                        ? 'text-[var(--a-danger)] hover:bg-[var(--a-danger-soft)]'
                        : 'text-[var(--a-text)] hover:bg-[var(--a-surface-2)]',
                      isHighlighted && (item.destructive ? 'bg-[var(--a-danger-soft)]' : 'bg-[var(--a-surface-2)]'),
                    )}
                    onClick={() => handleItemClick(item)}
                    onMouseEnter={() => {
                      if (!item.disabled) {
                        setHighlightedIndex(clickableIndex);
                      }
                    }}
                  >
                    {item.icon && (
                      <span className="grid h-4 w-4 shrink-0 place-items-center text-[var(--a-text-soft)]">
                        {item.icon}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {item.shortcut && (
                      <span className="ml-auto rounded-md bg-[var(--a-surface-2)] px-1.5 py-0.5 text-[11px] text-[var(--a-text-faint)]">
                        {item.shortcut}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

DropdownMenu.displayName = 'DropdownMenu';
